package com.bikompanion.ui.home

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.EnterExitState
import androidx.compose.animation.core.EaseInCubic
import androidx.compose.animation.core.EaseOutCubic
import androidx.compose.animation.core.animateDp
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.animation.scaleOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.PedalBike
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import dev.chrisbanes.haze.HazeInput
import dev.chrisbanes.haze.HazeState
import dev.chrisbanes.haze.glass.hazeGlass
import dev.chrisbanes.haze.hazeSource
import com.bikompanion.ui.composables.defaultGlassStyle
import com.bikompanion.ui.composables.glassPlateAlpha
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.strings.LocalStrings

// Eased tweens rather than springs: a spring's fast initial velocity read as a snap, while a
// soft ease-out lets the button settle in.
private val CheckBikesMotion = tween<Float>(durationMillis = 420, easing = EaseOutCubic)
private val CheckBikesExitMotion = tween<Float>(durationMillis = 260, easing = EaseInCubic)
private val CheckBikesOffsetMotion = tween<IntOffset>(durationMillis = 420, easing = EaseOutCubic)
private val CheckBikesExitOffsetMotion = tween<IntOffset>(durationMillis = 260, easing = EaseInCubic)

// Above the nav bar's source (z-index 1), so a sheet can refract both the bar and this action.
private const val CheckBikesHazeZIndex = 2f

// Android elevation shadows extend outside a composable's measured bounds. This reserved area
// keeps the entire shadow inside AnimatedVisibility's layer while it is being translated.
private val CheckBikesShadowInset = 12.dp

/** A contextual bike action that rests immediately above the floating navigation bar. */
@Composable
internal fun CheckBikesGlassButton(
    visible: Boolean,
    hazeState: HazeState,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    AnimatedVisibility(
        visible = visible,
        enter = slideInVertically(animationSpec = CheckBikesOffsetMotion) { it / 3 } +
            scaleIn(animationSpec = CheckBikesMotion, initialScale = 0.88f) +
            fadeIn(animationSpec = CheckBikesMotion),
        // Exit upward, away from the bar. Sliding back behind the bar makes the action appear
        // stationary until its fade has nearly completed on Android.
        exit = slideOutVertically(animationSpec = CheckBikesExitOffsetMotion) { -it / 4 } +
            scaleOut(animationSpec = CheckBikesExitMotion, targetScale = 0.92f) +
            fadeOut(animationSpec = CheckBikesExitMotion),
        // Haze source ordering does not control Compose's Android draw order. Keep the action
        // above the nav bar while its enter slide temporarily overlaps the bar's clipped layer.
        modifier = modifier.zIndex(CheckBikesHazeZIndex),
    ) {
        // Animate elevation with the surface so its opacity and spread arrive with the button.
        val shadowElevation by transition.animateDp(
            transitionSpec = {
                tween(durationMillis = 420, easing = EaseOutCubic)
            },
            label = "checkBikesShadowElevation",
        ) { state ->
            if (state == EnterExitState.Visible) 6.dp else 0.dp
        }
        val glassTint = MaterialTheme.colorScheme.primaryContainer
        val glassStyle = remember(glassTint) { defaultGlassStyle(glassTint, CircleShape) }
        val container = MaterialTheme.colorScheme.surfaceContainer
        // Match the bar's selected item: the action gets its emphasis from the translucent
        // primary-container plate, while its label continues to read as bar content.
        val actionPlate = MaterialTheme.colorScheme.primaryContainer.copy(alpha = glassPlateAlpha(0.6f))
        val iconColor = MaterialTheme.colorScheme.onPrimaryContainer
        val labelColor = MaterialTheme.colorScheme.onSurface
        Box(modifier = Modifier.padding(CheckBikesShadowInset)) {
            Row(
                modifier = Modifier
                    .shadow(shadowElevation, CircleShape, clip = false)
                    // The sheet is drawn after this button. Registering it as a source makes the
                    // sheet's glass show the action through it, just like it shows the nav bar.
                    .hazeSource(hazeState, zIndex = CheckBikesHazeZIndex)
                    // Inside the source: haze records the node's content as a rectangle, so a clip
                    // placed before it only rounds what is drawn on screen, not what a sheet samples.
                    .clip(CircleShape)
                    .hazeGlass(
                        input = HazeInput.Sources(hazeState),
                        style = glassStyle,
                    ).background(actionPlate)
                    .border(1.dp, MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f), CircleShape)
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        role = Role.Button,
                        onClick = onClick,
                    ).padding(horizontal = 20.dp, vertical = 14.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Icon(
                    imageVector = Icons.Default.PedalBike,
                    contentDescription = null,
                    tint = iconColor,
                    modifier = Modifier.padding(end = 8.dp),
                )
                Text(
                    text = LocalStrings.current.actionCheckRateBike,
                    color = labelColor,
                    style = MaterialTheme.typography.labelLarge,
                )
            }
        }
    }
}

@Preview
@Composable
private fun CheckBikesGlassButtonPreview() {
    GlassPreviewSurface { hazeState ->
        CheckBikesGlassButton(visible = true, hazeState = hazeState, onClick = {})
    }
}
